'use client';
import React, { useState, useMemo } from 'react';
import { Search, SlidersHorizontal, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '@/components/ui/StatusBadge';
import InvestmentDetailModal from './InvestmentDetailModal';

const opportunities = [
  {
    id: 'inv-001',
    title: 'Halal Food Manufacturing Expansion',
    company: 'Nusantara Halal Foods Sdn Bhd',
    country: 'Malaysia',
    flag: '🇲🇾',
    sector: 'Halal Food',
    stage: 'Series A',
    fundingRequired: 'US$2.5M',
    fundingUSD: 2500000,
    shariahStatus: 'Completed',
    ddScore: 88,
    expectedUse: 'Factory expansion, cold chain infrastructure, export certification',
    description: 'Nusantara Halal Foods seeks Series A capital to expand its JAKIM-certified frozen beef facility. Target export markets include China, UAE, and Saudi Arabia. Existing revenue of RM 18M with 42% YoY growth.',
    financials: { revenue: 'RM 18M', growth: '42% YoY', ebitda: 'RM 3.2M', employees: 148 },
    esgImpact: 'Direct employment for 148 rural workers. Halal supply chain integrity supports Muslim consumer trust globally.',
    corridors: ['Malaysia → China', 'Malaysia → GCC'],
    documents: ['Business Plan', 'Audited Financials 2025', 'Shariah Review Report', 'Export Licence'],
    interestCount: 12,
  },
  {
    id: 'inv-002',
    title: 'Ethical Halal Cosmetics Brand',
    company: 'PureEthic Beauty LLC',
    country: 'United States',
    flag: '🇺🇸',
    sector: 'Cosmetics',
    stage: 'Seed',
    fundingRequired: 'US$1.2M',
    fundingUSD: 1200000,
    shariahStatus: 'In Progress',
    ddScore: 81,
    expectedUse: 'Product line expansion, IFANCA certification, Southeast Asia market entry',
    description: 'PureEthic Beauty is a US-based halal cosmetics brand targeting Muslim-majority markets in Southeast Asia and GCC. IFANCA certified with 3 SKUs in market. Seeking seed capital for scaling.',
    financials: { revenue: 'US$420K', growth: '68% YoY', ebitda: 'US$-80K', employees: 22 },
    esgImpact: 'Cruelty-free, alcohol-free, ethically sourced ingredients. Female-founded and led.',
    corridors: ['US → Malaysia', 'US → GCC'],
    documents: ['Pitch Deck', 'Product Catalogue', 'IFANCA Certificate'],
    interestCount: 7,
  },
  {
    id: 'inv-003',
    title: 'China Halal Export Onboarding Program',
    company: 'Zhejiang Halal Export Consortium',
    country: 'China',
    flag: '🇨🇳',
    sector: 'Manufacturing / Export',
    stage: 'Strategic Partnership',
    fundingRequired: 'US$4.0M',
    fundingUSD: 4000000,
    shariahStatus: 'Completed',
    ddScore: 84,
    expectedUse: 'Certification onboarding for 80+ Chinese manufacturers, digital export readiness platform',
    description: 'A consortium-led initiative to onboard 80+ Chinese halal food and ingredient manufacturers onto the HalalCham registry, targeting OIC market access. Backed by Zhejiang provincial trade authority.',
    financials: { revenue: 'CNY 42M', growth: '28% YoY', ebitda: 'CNY 8.4M', employees: 340 },
    esgImpact: 'Supports halal standards harmonization between China and OIC. Enables 80+ SMEs to access Muslim markets.',
    corridors: ['China → OIC Markets', 'China → Malaysia'],
    documents: ['Consortium Agreement', 'Provincial Trade Authority Letter', 'Shariah Review Report', 'Audited Financials'],
    interestCount: 19,
  },
  {
    id: 'inv-004',
    title: 'OIC Food Security Agriculture Project',
    company: 'Agri Tayyib Consortium',
    country: 'Indonesia',
    flag: '🇮🇩',
    sector: 'Agriculture',
    stage: 'Infrastructure Growth',
    fundingRequired: 'US$7.5M',
    fundingUSD: 7500000,
    shariahStatus: 'Completed',
    ddScore: 90,
    expectedUse: 'Halal-certified agricultural infrastructure, irrigation, and supply chain across 3 OIC countries',
    description: 'A multi-country halal agriculture project spanning Indonesia, Malaysia, and Türkiye. Targets food security for OIC nations through certified halal farming inputs and logistics. MUI and JAKIM reviewed.',
    financials: { revenue: 'IDR 180B', growth: '18% YoY', ebitda: 'IDR 32B', employees: 820 },
    esgImpact: 'Food security for 3 OIC nations. Sustainable halal farming practices. 820+ rural employment.',
    corridors: ['Indonesia → OIC Markets', 'Malaysia → OIC Markets'],
    documents: ['Multi-Country MOU', 'MUI Shariah Review', 'JAKIM Endorsement', 'Feasibility Study'],
    interestCount: 31,
  },
  {
    id: 'inv-005',
    title: 'Islamic Fintech Payment Gateway',
    company: 'TakafulPay Technologies',
    country: 'UAE',
    flag: '🇦🇪',
    sector: 'Islamic Finance',
    stage: 'Series A',
    fundingRequired: 'US$3.2M',
    fundingUSD: 3200000,
    shariahStatus: 'Completed',
    ddScore: 86,
    expectedUse: 'Payment infrastructure for halal e-commerce, Takaful integration, GCC market rollout',
    description: 'TakafulPay is a Shariah-compliant payment gateway enabling halal e-commerce merchants to offer Islamic payment options including Murabahah installments and Takaful-backed buyer protection.',
    financials: { revenue: 'AED 6.8M', growth: '94% YoY', ebitda: 'AED 1.1M', employees: 56 },
    esgImpact: 'Enables ethical, interest-free commerce for Muslim consumers across GCC and Southeast Asia.',
    corridors: ['UAE → GCC', 'UAE → Malaysia'],
    documents: ['Pitch Deck', 'Shariah Compliance Certificate', 'CBUAE Licence', 'Audited Financials'],
    interestCount: 24,
  },
  {
    id: 'inv-006',
    title: 'Halal Logistics Cold Chain Network',
    company: 'TrustFreight Asia Pte Ltd',
    country: 'Singapore',
    flag: '🇸🇬',
    sector: 'Logistics',
    stage: 'Growth Equity',
    fundingRequired: 'US$5.8M',
    fundingUSD: 5800000,
    shariahStatus: 'In Progress',
    ddScore: 83,
    expectedUse: 'Cold chain hub expansion in Malaysia, Indonesia, and UAE for halal-segregated freight',
    description: 'TrustFreight Asia operates MUIS-certified halal-segregated cold chain logistics across Southeast Asia. Seeking growth equity to expand into UAE and Saudi Arabia with three new hub facilities.',
    financials: { revenue: 'SGD 14.2M', growth: '31% YoY', ebitda: 'SGD 2.8M', employees: 192 },
    esgImpact: 'Halal supply chain integrity across 6 countries. Reduces food waste through cold chain optimization.',
    corridors: ['Singapore → Malaysia', 'Singapore → GCC'],
    documents: ['Business Plan', 'MUIS Certificate', 'Expansion Feasibility', 'Audited Financials 2025'],
    interestCount: 15,
  },
];

type Opportunity = typeof opportunities[0];

const sectors = ['All Sectors', 'Halal Food', 'Cosmetics', 'Manufacturing / Export', 'Agriculture', 'Islamic Finance', 'Logistics'];
const stages = ['All Stages', 'Seed', 'Series A', 'Growth Equity', 'Strategic Partnership', 'Infrastructure Growth'];
const countryOptions = ['All Countries', 'Malaysia', 'China', 'United States', 'Indonesia', 'UAE', 'Singapore'];
const shariahOptions = ['All Shariah Status', 'Completed', 'In Progress'];

export default function InvestmentExchangeClient() {
  const [search, setSearch] = useState('');
  const [selectedSector, setSelectedSector] = useState('All Sectors');
  const [selectedStage, setSelectedStage] = useState('All Stages');
  const [selectedCountry, setSelectedCountry] = useState('All Countries');
  const [selectedShariah, setSelectedShariah] = useState('All Shariah Status');
  const [maxTicket, setMaxTicket] = useState(10);
  const [filterOpen, setFilterOpen] = useState(true);
  const [selectedOpp, setSelectedOpp] = useState<Opportunity | null>(null);

  const filtered = useMemo(() => {
    return opportunities.filter((o) => {
      const matchSearch = !search || o.title.toLowerCase().includes(search.toLowerCase()) || o.company.toLowerCase().includes(search.toLowerCase());
      const matchSector = selectedSector === 'All Sectors' || o.sector === selectedSector;
      const matchStage = selectedStage === 'All Stages' || o.stage === selectedStage;
      const matchCountry = selectedCountry === 'All Countries' || o.country === selectedCountry;
      const matchShariah = selectedShariah === 'All Shariah Status' || o.shariahStatus === selectedShariah;
      const matchTicket = o.fundingUSD / 1000000 <= maxTicket;
      return matchSearch && matchSector && matchStage && matchCountry && matchShariah && matchTicket;
    });
  }, [search, selectedSector, selectedStage, selectedCountry, selectedShariah, maxTicket]);

  const ddColor = (score: number) => {
    if (score >= 88) return 'var(--emerald-bright)';
    if (score >= 80) return 'var(--gold)';
    return 'var(--warning)';
  };

  return (
    <div className="max-w-screen-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Halal Investment Exchange</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
            {filtered.length} Shariah-compliant opportunities across {opportunities.length} total listings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ background: 'rgba(201, 168, 76, 0.1)', border: '1px solid rgba(201, 168, 76, 0.25)', color: 'var(--gold)' }}
          >
            <TrendingUp size={13} />
            US${(opportunities.reduce((a, o) => a + o.fundingUSD, 0) / 1000000).toFixed(1)}M Total Pipeline
          </div>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="btn-outline flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
          >
            <SlidersHorizontal size={15} />
            Filters
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Filter Panel */}
        {filterOpen && (
          <aside
            className="w-60 xl:w-72 shrink-0 rounded-2xl p-5 shadow-card self-start sticky top-6 space-y-5"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Search
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Company or opportunity..."
                  className="w-full pl-8 pr-3 py-2 rounded-lg text-sm input-field"
                />
              </div>
            </div>

            {[
              { label: 'Sector', value: selectedSector, set: setSelectedSector, options: sectors, key: 'sector' },
              { label: 'Stage', value: selectedStage, set: setSelectedStage, options: stages, key: 'stage' },
              { label: 'Country', value: selectedCountry, set: setSelectedCountry, options: countryOptions, key: 'invcountry' },
              { label: 'Shariah Status', value: selectedShariah, set: setSelectedShariah, options: shariahOptions, key: 'shariah' },
            ].map((filter) => (
              <div key={`invfilter-${filter.key}`}>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                  {filter.label}
                </label>
                <select
                  value={filter.value}
                  onChange={(e) => filter.set(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm input-field"
                >
                  {filter.options.map((o) => <option key={`${filter.key}-${o}`} value={o}>{o}</option>)}
                </select>
              </div>
            ))}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Max Ticket: <span style={{ color: 'var(--gold)' }}>US${maxTicket}M</span>
              </label>
              <input
                type="range"
                min={1}
                max={10}
                step={0.5}
                value={maxTicket}
                onChange={(e) => setMaxTicket(Number(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                <span>US$1M</span><span>US$10M+</span>
              </div>
            </div>

            <button
              onClick={() => { setSearch(''); setSelectedSector('All Sectors'); setSelectedStage('All Stages'); setSelectedCountry('All Countries'); setSelectedShariah('All Shariah Status'); setMaxTicket(10); }}
              className="w-full btn-outline py-2 rounded-lg text-sm flex items-center justify-center gap-2"
            >
              <X size={13} /> Clear Filters
            </button>
          </aside>
        )}

        {/* Opportunity Cards */}
        <div className="flex-1 min-w-0">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                <TrendingUp size={28} style={{ color: 'var(--muted-foreground)' }} />
              </div>
              <p className="text-base font-semibold mb-2">No opportunities match your filters</p>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Adjust your sector, stage, or ticket size filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-5">
              {filtered.map((opp) => (
                <div
                  key={opp.id}
                  className="rounded-2xl shadow-card transition-all duration-200 hover:-translate-y-1 flex flex-col"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                >
                  {/* Card Header */}
                  <div
                    className="px-5 pt-5 pb-4 border-b"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-lg">{opp.flag}</span>
                          <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>{opp.country}</span>
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.2)' }}
                          >
                            {opp.stage}
                          </span>
                        </div>
                        <h3 className="text-base font-bold leading-snug">{opp.title}</h3>
                        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{opp.company}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-extrabold font-mono-data" style={{ color: 'var(--gold)' }}>{opp.fundingRequired}</p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Funding Required</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1.5 justify-end mb-1">
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Due Diligence</span>
                          <span className="text-sm font-bold font-mono-data" style={{ color: ddColor(opp.ddScore) }}>{opp.ddScore}%</span>
                        </div>
                        <div className="w-24 h-1.5 rounded-full ml-auto" style={{ background: 'var(--muted)' }}>
                          <div className="h-1.5 rounded-full" style={{ width: `${opp.ddScore}%`, background: ddColor(opp.ddScore) }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-5 py-4 flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Sector</span>
                      <span className="text-xs font-semibold">{opp.sector}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Shariah Review</span>
                      <StatusBadge
                        variant={opp.shariahStatus === 'Completed' ? 'completed' : 'progress'}
                        label={opp.shariahStatus}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Investor Interest</span>
                      <span className="text-xs font-semibold" style={{ color: 'var(--info)' }}>{opp.interestCount} investors</span>
                    </div>

                    <div>
                      <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Expected Use of Funds</p>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--secondary-foreground)' }}>{opp.expectedUse}</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Market Corridors</p>
                      <div className="flex flex-wrap gap-1">
                        {opp.corridors.map((corridor) => (
                          <span
                            key={`corridor-${opp.id}-${corridor}`}
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)', border: '1px solid rgba(59,130,246,0.2)' }}
                          >
                            {corridor}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="px-5 pb-5 flex gap-2">
                    <button
                      onClick={() => setSelectedOpp(opp)}
                      className="flex-1 btn-primary py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5"
                    >
                      <TrendingUp size={13} />
                      View Details
                    </button>
                    <button
                      onClick={() => toast.success('Investment pack requested')}
                      className="flex-1 btn-gold py-2.5 rounded-xl text-xs font-semibold"
                    >
                      Request Investor Pack
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedOpp && (
        <InvestmentDetailModal opportunity={selectedOpp} onClose={() => setSelectedOpp(null)} />
      )}
    </div>
  );
}